import { namespace } from '@common/config/constants';
import { getTaskSumByBoardIdRecord } from '@common-reducers/CampaignSelectors';
import { resolveCampaignCardStatus, type CampaignCardStatus } from '@common/services/campaign-status';
import { campaignsSelectOneObjectByQuery } from '@common/reducers/DBServiceThunks';
import { useAppDispatch, useAppSelector } from '@common/reducers/hooks/store.hook';
import { revealCalendarOccurrence } from '@common-reducers/CalendarReducer';
// The identity palette the calendar grid uses, so a campaign carries one colour
// in the list and on the grid instead of the flat sage every campaign shared.
import { chipColorFor } from '@common/utils/calendar-palette';
import { InProgressCampaignIndicator } from '@extension/context/content/components/bt-bulk-dashboard/in-progress-campaign-indicator';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useOpenCampaignPage } from '@common-views/campaigns-redesign/open-campaign-page';
import PlannerListRow, { type PlannerRowTone } from './planner-list-row';
import { campaignRecipientCount } from '@common/components/destination-avatars/destination-plan';
import { useAudienceMemberHints, useCampaignDestination } from '@common/components/destination-avatars/use-campaign-destination';

type Props = {
    campaignId: string;
}

/** Status label + tone, keyed on the SAME resolver the campaign cards use, so a
 *  campaign cannot read one way in the list and another on its card. The old row
 *  rendered a badge for `pending` only: paused, stopped and finished campaigns
 *  all looked like healthy scheduled ones. */
const STATUS_PRESENTATION: Partial<Record<CampaignCardStatus, { key: string; fallback: string; tone: PlannerRowTone }>> = {
    draft: { key: 'calendar.sidebar.status.draft', fallback: 'Draft', tone: 'neutral' },
    pending: { key: 'calendar.sidebar.status.scheduled', fallback: 'Scheduled', tone: 'brand' },
    active: { key: 'calendar.sidebar.status.sending', fallback: 'Sending', tone: 'brand' },
    running: { key: 'calendar.sidebar.status.sending', fallback: 'Sending', tone: 'brand' },
    paused: { key: 'calendar.sidebar.status.paused', fallback: 'Paused', tone: 'warn' },
    completed: { key: 'calendar.sidebar.status.sent', fallback: 'Sent', tone: 'ok' },
    aborted: { key: 'calendar.sidebar.status.stopped', fallback: 'Stopped', tone: 'bad' },
    expired: { key: 'calendar.sidebar.status.expired', fallback: 'Expired', tone: 'bad' },
};

/** BE#154's terminal "sent, but some recipients had no WhatsApp id". The shared
 *  card resolver does not know it yet and would call it a draft, which is both
 *  wrong and alarming on a campaign that has finished. */
const SENT_WITH_SKIPS = {
    key: 'calendar.sidebar.status.sentWithSkips', fallback: 'Sent, some skipped', tone: 'warn' as PlannerRowTone,
};

export default function CalendarSidebarCampaignRow({ campaignId }: Props) {
    const { t } = useTranslation(namespace);
    const campaign = useAppSelector(state => campaignsSelectOneObjectByQuery(state.DBCampaignReducer, { _id: campaignId }))
    const taskSumByBoardIdRecord = useAppSelector(getTaskSumByBoardIdRecord);
    // A drip's steps share the drip's colour; a standalone campaign gets its own.
    const identityColor = chipColorFor(campaign?.dripId ?? campaignId);
    const openCampaignPage = useOpenCampaignPage();
    const dispatch = useAppDispatch();
    // The chip on the grid carries this campaign document's id and due date.
    const dueDate = campaign?.schedule?.dueDate;
    const reveal = campaign?._id && dueDate
        ? () => dispatch(revealCalendarOccurrence({ id: String(campaign._id), isCampaign: true, dueDate: new Date(dueDate as any).toISOString() }))
        : undefined;

    // A click opens the campaign's page — the same place a click on its
    // calendar chip goes, where it is viewed and edited. (It used to open an
    // actions menu; everything that menu offered lives on the page.) A step of a
    // sequence opens the whole sequence, by the drip's id.
    const handleRowClick = (e: React.MouseEvent) => {
        // Only trigger if it's a left click
        if (e.button !== 0) return;
        openCampaignPage(campaign?.dripId ? String(campaign.dripId) : campaignId, campaign?.dripStepIndex, 'calendar');
    };

    // Total number of contacts — the one count the hover card uses too.
    const contactCount = campaignRecipientCount(campaign, taskSumByBoardIdRecord);

    // WHO it goes to: up to five faces, then "+N". The count is the SAME
    // `contactCount` the "N contacts" detail prints, so the chip and the text
    // cannot disagree. Faces come from audience rows already in the store, else
    // from one batched preview read shared by every row on screen — never a
    // request per row (see audience-preview-loader.ts).
    const destination = useCampaignDestination(campaign, contactCount);
    // The previewed members' names, for the hover list — from the same read.
    const destinationHints = useAudienceMemberHints(destination);

    const status = campaign?.campaignStatus ?? '';
    const cardStatus = resolveCampaignCardStatus({
        campaignStatus: status,
        contactCount: campaign?.contactCount,
        isRecurring: campaign?.schedule?.isRecurring,
        lastActivityAt: (campaign as any)?.updatedAt ?? campaign?.schedule?.dueDate,
    }, new Date());
    const presentation = status === 'complete-with-skips' ? SENT_WITH_SKIPS : STATUS_PRESENTATION[cardStatus];

    return (
                    <PlannerListRow
                        railColor={identityColor}
                        kind="campaign"
                        destination={destination}
                        destinationHints={destinationHints}
                        // The name identifies the campaign; the message is the
                        // preview. The old row led with the message text and
                        // printed the raw Mongo id beside it.
                        // A named campaign leads with its name and previews the
                        // message; an unnamed one leads with the message, which
                        // is the only thing that identifies it. (The old row
                        // always led with the message — and printed the raw
                        // Mongo id next to it.)
                        title={campaign?.name || campaign?.template?.message || t('campaign.unnamed', 'Unnamed campaign')}
                        preview={campaign?.name ? (campaign?.template?.message || undefined) : undefined}
                        detail={t('calendar.contactsCount', { count: contactCount, defaultValue: `${contactCount} contacts` })}
                        dueDate={campaign?.schedule?.dueDate}
                        status={presentation
                            ? { label: t(presentation.key, presentation.fallback), tone: presentation.tone }
                            : undefined}
                        extra={cardStatus === 'running' ? (
                            <InProgressCampaignIndicator
                                key={campaign?._id}
                                campaignId={campaign?._id ?? ''}
                                onClick={() => { }}
                                compact={true}
                            />
                        ) : undefined}
                        onClick={handleRowClick}
                        onReveal={reveal}
                    />
    )
}
